<script>
import { componentStore } from "@common/componentStore/componentStore";
import { defineAsyncComponent } from "@common/defineAsyncComponent";

const editorSettings = {};

const unRefPlacePickerConfig = {
    cId: "unRefPlacePicker",
    cType: "unRefPlacePicker",
    cTitle: "Remove Assignments Picker",
    cTemplate: `<div class="urp-dialog-body">
        <div v-if="loading" class="urp-loading" role="status" aria-live="polite">
            <i class="fa-solid fa-spinner fa-spin urp-loading-icon" aria-hidden="true"></i>
            <p class="urp-text urp-loading-text">Loading assignments…</p>
        </div>
        <div v-else-if="loadError" class="urp-empty">
            <div class="urp-error" role="alert">
                <i class="fa-solid fa-triangle-exclamation urp-warning-icon" aria-hidden="true"></i>
                <p class="urp-warning-text">{{ loadError }}</p>
            </div>
            <div class="urp-actions">
                <button type="button" class="urp-btn-secondary" @click="cancel()">Close</button>
                <button type="button" class="urp-btn-primary" @click="loadAssignments()">Retry</button>
            </div>
        </div>
        <div v-else-if="processing" class="urp-loading" role="status" aria-live="polite">
            <i class="fa-solid fa-spinner fa-spin urp-loading-icon" aria-hidden="true"></i>
            <div class="urp-loading-copy">
                <p class="urp-text urp-loading-text">Removing assignments…</p>
                <p class="urp-text urp-helper-text">This may take a few seconds.</p>
            </div>
        </div>
        <div v-else-if="processResult" class="urp-empty">
            <div :class="processResult.success ? 'urp-success' : 'urp-error'" :role="processResult.success ? 'status' : 'alert'">
                <i :class="processResult.success ? 'fa-solid fa-circle-check urp-success-icon' : 'fa-solid fa-triangle-exclamation urp-warning-icon'" aria-hidden="true"></i>
                <div>
                    <p class="urp-result-title">{{ processResult.title }}</p>
                    <p v-for="(message, index) in processResult.messageLines" :key="index" class="urp-warning-text">{{ message }}</p>
                </div>
            </div>
            <div class="urp-actions">
                <button v-if="!processResult.success" type="button" class="urp-btn-secondary" @click="finish()">Close</button>
                <button v-if="!processResult.success" type="button" class="urp-btn-primary" @click="backToSelection()">Back to selection</button>
                <button v-else type="button" class="urp-btn-primary" @click="finish()">Done</button>
            </div>
        </div>
        <div v-else-if="rawCount === 0" class="urp-empty">
            <p class="urp-text urp-empty-text">This volunteer has no removable assignments.</p>
            <div class="urp-actions">
                <button type="button" class="urp-btn-secondary" @click="cancel()">Close</button>
            </div>
        </div>
        <template v-else>
            <div class="urp-mode-picker">
                <control name="MODE" :prop-config="controls.MODE" :value="controls.MODE.value" @update:value="setMode($event)"></control>
            </div>
            <hr class="urp-divider" aria-hidden="true">
            <div v-if="mode === 'individual'" class="urp-mode-section">
                <div v-if="!rows.length" class="urp-mode-empty urp-text">
                    No individually-removable assignments of this type — try Organization or Remove All mode instead.
                </div>
                <template v-else>
                    <div class="urp-list-header">
                        <span class="urp-section-label">Assignments</span>
                        <span class="urp-count">{{ checkedCount }} selected of {{ rows.length }}</span>
                    </div>
                    <div class="urp-list">
                        <control name="INDIVIDUAL_LIST" :prop-config="controls.INDIVIDUAL_LIST" :value="controls.INDIVIDUAL_LIST.value" @update:value="setChecked($event)"></control>
                    </div>
                </template>
            </div>
            <div v-if="mode === 'org'" class="urp-mode-section">
                <control name="ORG" :prop-config="controls.ORG" :value="controls.ORG.value" @update:value="setOrgId($event)"></control>
                <div v-if="orgId" class="urp-warning" role="status" aria-live="polite">
                    <i class="fa-solid fa-triangle-exclamation urp-warning-icon" aria-hidden="true"></i>
                    <p class="urp-warning-text">
                        This will remove {{ orgCount }} assignment{{ orgCount === 1 ? '' : 's' }} this volunteer has with {{ orgTitle }}<span
                            v-if="orgSubOrgCount > 0"
                        > (including {{ orgSubOrgCount }} with its sub-organizations)</span>.
                    </p>
                </div>
            </div>
            <div v-if="mode === 'all'" class="urp-mode-section">
                <div class="urp-warning" role="status" aria-live="polite">
                    <i class="fa-solid fa-triangle-exclamation urp-warning-icon" aria-hidden="true"></i>
                    <p class="urp-warning-text">This will remove all {{ rawCount }} assignments for this volunteer.</p>
                </div>
            </div>
            <div class="urp-actions">
                <button type="button" class="urp-btn-secondary" @click="cancel()">Cancel</button>
                <button type="button" class="urp-btn-danger" :disabled="submitDisabled" @click="submit()">{{ submitLabel }}</button>
            </div>
        </template>
    </div>`
};

const buildPickerControls = (rows = [], orgs = []) => ({
    MODE: {
        name: 'MODE',
        type: 'radio_button_list',
        removeOnDestroy: true,
        fields: { text: 'text', value: 'value' },
        items: [
            { text: 'Select Individually', value: 'individual' },
            { text: 'Remove All for an Organization', value: 'org' },
            { text: 'Remove All', value: 'all' }
        ],
        value: 'individual'
    },
    INDIVIDUAL_LIST: {
        name: 'INDIVIDUAL_LIST',
        type: 'checkbox_list',
        removeOnDestroy: true,
        items: rows.map((r) => ({ value: r.key, text: r.label })),
        value: {}
    },
    ORG: {
        name: 'ORG',
        type: 'dropdown',
        removeOnDestroy: true,
        fields: { text: 'text', value: 'value' },
        items: orgs.map((o) => ({ value: o.id, text: o.title })),
        placeholder: 'Select an organization…',
        value: false
    }
});

const unRefPlacePickerCmp = {
    name: "unRefPlacePicker",
    editorSettings: editorSettings,
    props: {
        volId: { default: false },
        targetType: { type: String, default: 'opp' },
        isReferral: { default: false },
        dialogState: { type: Object, default: () => ({}) }
    },
    data() {
        return {
            loading: true,
            loadError: false,
            processing: false,
            processResult: false,
            mode: 'individual',
            checkedMap: {},
            orgId: false,
            rows: [],
            orgs: [],
            orgDescendants: {},
            rawCount: 0,
            rawCountByOrg: {},
            controls: buildPickerControls()
        };
    },
    computed: {
        checkedCount() {
            return Object.values(this.checkedMap).filter(Boolean).length;
        },
        orgTitle() {
            const org = this.orgs.find((o) => o.id === this.orgId);
            return org ? org.title : '';
        },
        orgCount() {
            const descendantIds = this.orgDescendants[this.orgId] ? Array.from(this.orgDescendants[this.orgId]) : [];
            return [this.orgId, ...descendantIds].reduce((sum, id) => sum + (this.rawCountByOrg[id] || 0), 0);
        },
        orgSubOrgCount() {
            return this.orgCount - (this.rawCountByOrg[this.orgId] || 0);
        },
        submitDisabled() {
            if (this.loading || this.loadError) return true;
            if (this.mode === 'individual') return !this.checkedCount;
            if (this.mode === 'org') return !this.orgId;
            return false;
        },
        submitLabel() {
            if (this.mode === 'individual') return `Remove Selected Assignments (${this.checkedCount})`;
            if (this.mode === 'org') return this.orgId ? `Remove Assignments for Organization (${this.orgCount})` : 'Remove Assignments for Organization';
            return 'Remove All Assignments';
        }
    },
    methods: {
        async onLoad() {
            await this.loadAssignments();
        },
        async onPropsUpdate(prop) {
            if (['propVolId', 'propTargetType', 'propIsReferral'].includes(prop)) await this.loadAssignments();
        },
        requireSuccess(response, label) {
            if (response?.status !== 'success') {
                throw new Error(`${label} request failed`);
            }
        },
        resetLoadState() {
            this.processResult = false;
            this.mode = 'individual';
            this.checkedMap = {};
            this.orgId = false;
            this.rows = [];
            this.orgs = [];
            this.orgDescendants = {};
            this.rawCount = 0;
            this.rawCountByOrg = {};
            this.refreshControls();
        },
        async loadAssignments() {
            this.loading = true;
            this.loadError = false;
            this.resetLoadState();
            try {
                const resolved = this.targetType === 'sch'
                    ? await this.resolveShiftAssignments()
                    : await this.resolveOpportunityAssignments();
                this.rows = resolved.rows;
                this.orgs = Object.values(resolved.orgsById);
                this.rawCount = resolved.rawCount;
                this.rawCountByOrg = resolved.rawCountByOrg;
                this.orgDescendants = await this.resolveOrgDescendants(resolved.orgsById, this.orgs);
                this.refreshControls();
            } catch (error) {
                this.loadError = 'There was a problem loading this volunteer\'s assignments. Please try again.';
            } finally {
                this.loading = false;
            }
        },
        async resolveShiftAssignments() {
            const rows = [];
            const orgsById = {};
            let rawCount = 0;
            const rawCountByOrg = {};
            const ssFields = ['OPP_TITLE', 'OPPORTUNITY_ID', 'ORGANIZATION_ID', 'ORG_TITLE', 'SS_DATE', 'SS_START_TIME'];
            const [placedResponse, referredResponse] = await Promise.all([
                this.sDs.post('er_getPlacedSlotList', false, ssFields, false, false, false, this.volId, false),
                this.sDs.post('er_getReferredSlotList', false, ssFields, false, false, this.volId, false)
            ]);
            this.requireSuccess(placedResponse, 'Placed shift list');
            this.requireSuccess(referredResponse, 'Referred shift list');
            const placedData = placedResponse.data || {};
            const referredData = referredResponse.data || {};
            const allPlacedSchIds = new Set();
            const allReferredSchIds = new Set();
            Object.keys(placedData).forEach((schId) => allPlacedSchIds.add(schId));
            Object.keys(referredData).forEach((schId) => allReferredSchIds.add(schId));
            const schIdList = Array.from(new Set([...allPlacedSchIds, ...allReferredSchIds]));
            const includePlacements = this.isReferral === false || this.isReferral === 'both';
            const includeReferrals = this.isReferral === true || this.isReferral === 'both';
            schIdList.forEach((schId) => {
                const isPlaced = allPlacedSchIds.has(schId);
                const isReferred = allReferredSchIds.has(schId);
                const sch = placedData[schId] || referredData[schId] || {};
                const orgId = sch.ORGANIZATION_ID || false;
                const orgTitle = sch.ORG_TITLE || 'Unknown Organization';
                if (orgId && !orgsById[orgId]) orgsById[orgId] = { id: orgId, title: orgTitle };
                const contributions = (isPlaced ? 1 : 0) + (isReferred ? 1 : 0);
                rawCount += contributions;
                if (orgId) rawCountByOrg[orgId] = (rawCountByOrg[orgId] || 0) + contributions;
                if ((isPlaced && includePlacements) || (isReferred && includeReferrals)) {
                    const dateLabel = sch.SS_DATE ? (sch.SS_DATE + (sch.SS_START_TIME ? ' ' + sch.SS_START_TIME : '')) : 'Shift';
                    // oppId carried so Org/All-mode cleanup can also clear opp.schIds, not just vol.schIds
                    rows.push({
                        key: schId,
                        label: `${orgTitle} — ${sch.OPP_TITLE || 'Opportunity'} — ${dateLabel}`,
                        orgId,
                        oppId: sch.OPPORTUNITY_ID || false,
                        checked: false
                    });
                }
            });
            return {
                rows,
                orgsById,
                rawCount,
                rawCountByOrg
            };
        },
        async resolveOpportunityAssignments() {
            const rows = [];
            const orgsById = {};
            let rawCount = 0;
            const rawCountByOrg = {};
            const includePlacements = this.isReferral === false || this.isReferral === 'both';
            const includeReferrals = this.isReferral === true || this.isReferral === 'both';
            const oppFields = ['OPP_TITLE', 'OPP_ORGANIZATION_ID'];
            const [placedResponse, referredResponse] = await Promise.all([
                this.sDs.post('er_getPlacedOppList', false, oppFields, false, this.volId),
                this.sDs.post('er_getReferredOppList', false, oppFields, this.volId)
            ]);
            this.requireSuccess(placedResponse, 'Placed opportunity list');
            this.requireSuccess(referredResponse, 'Referred opportunity list');
            const placedData = placedResponse.data || {};
            const referredData = referredResponse.data || {};
            const allPlacedOppIds = new Set();
            const allReferredOppIds = new Set();
            Object.keys(placedData).forEach((oppId) => allPlacedOppIds.add(oppId));
            Object.keys(referredData).forEach((oppId) => allReferredOppIds.add(oppId));
            const oppIdList = Array.from(new Set([...allPlacedOppIds, ...allReferredOppIds]));
            if (oppIdList.length) {
                const orgIdSet = new Set();
                oppIdList.forEach((oppId) => {
                    const orgId = placedData[oppId]?.OPP_ORGANIZATION_ID || referredData[oppId]?.OPP_ORGANIZATION_ID;
                    if (orgId) orgIdSet.add(orgId);
                });
                let orgData = {};
                if (orgIdSet.size) {
                    const orgResponse = await this.sDs.post('sds_getProfileField', 'org', Array.from(orgIdSet), ['ORG_TITLE']);
                    this.requireSuccess(orgResponse, 'Organization title');
                    orgData = orgResponse.data || {};
                }
                oppIdList.forEach((oppId) => {
                    const isPlaced = allPlacedOppIds.has(oppId);
                    const isReferred = allReferredOppIds.has(oppId);
                    const opp = placedData[oppId] || referredData[oppId] || {};
                    const orgId = opp.OPP_ORGANIZATION_ID || false;
                    const orgTitle = (orgId && orgData[orgId]?.ORG_TITLE) || 'Unknown Organization';
                    if (orgId && !orgsById[orgId]) orgsById[orgId] = { id: orgId, title: orgTitle };
                    // Counted once per placed/referred contribution, not once per opp — an opp the
                    // volunteer is both placed AND referred to is 2 removable assignments, not 1.
                    const contributions = (isPlaced ? 1 : 0) + (isReferred ? 1 : 0);
                    rawCount += contributions;
                    if (orgId) rawCountByOrg[orgId] = (rawCountByOrg[orgId] || 0) + contributions;
                    if ((isPlaced && includePlacements) || (isReferred && includeReferrals)) {
                        rows.push({
                            key: oppId,
                            label: `${orgTitle} — ${opp.OPP_TITLE || 'Opportunity'}`,
                            orgId,
                            checked: false
                        });
                    }
                });
            }
            return {
                rows,
                orgsById,
                rawCount,
                rawCountByOrg
            };
        },
        async resolveOrgDescendants(orgsById, orgs) {
            const orgDescendants = {};
            if (orgs.length > 1) {
                const relResponse = await this.sDs.post('sds_getOrgRelationshipList', 0, 0, []);
                this.requireSuccess(relResponse, 'Organization relationship');
                const relRows = Array.isArray(relResponse.data) ? relResponse.data : [];
                const childrenByParent = {};
                relRows.forEach((rel) => {
                    const parentId = String(rel.PARENT_ORGANIZATION_ID);
                    const childId = String(rel.CHILD_ORGANIZATION_ID);
                    if (!childrenByParent[parentId]) childrenByParent[parentId] = [];
                    childrenByParent[parentId].push(childId);
                });
                Object.keys(orgsById).forEach((rootOrgId) => {
                    const descendants = new Set();
                    const queue = [...(childrenByParent[rootOrgId] || [])];
                    while (queue.length) {
                        const curOrgId = queue.shift();
                        if (curOrgId === rootOrgId || descendants.has(curOrgId)) continue;
                        descendants.add(curOrgId);
                        queue.push(...(childrenByParent[curOrgId] || []));
                    }
                    orgDescendants[rootOrgId] = descendants;
                });
            }
            return orgDescendants;
        },
        refreshControls() {
            const rows = Array.isArray(this.rows) ? this.rows : [];
            const orgs = Array.isArray(this.orgs) ? this.orgs : [];
            const controls = buildPickerControls(rows, orgs);
            controls.MODE.value = this.mode;
            controls.INDIVIDUAL_LIST.value = this.checkedMap || {};
            controls.ORG.value = this.orgId || false;
            this.controls = controls;
        },
        setMode(value) {
            this.mode = value;
            this.controls.MODE.value = value;
        },
        setOrgId(value) {
            this.orgId = value;
            this.controls.ORG.value = value;
        },
        setChecked(valueMap) {
            this.checkedMap = valueMap || {};
            this.controls.INDIVIDUAL_LIST.value = this.checkedMap;
        },
        cancel() {
            if (this.processing) return;
            library.hideDialog(this.contextDialogId, false);
        },
        buildSelectionPayload() {
            return {
                mode: this.mode,
                orgId: this.orgId,
                orgTitle: this.orgTitle,
                orgCount: this.orgCount,
                selectedKeys: Object.keys(this.checkedMap).filter((k) => this.checkedMap[k]),
                rows: this.rows,
                orgDescendants: this.orgDescendants,
                rawCount: this.rawCount,
                rawCountByOrg: this.rawCountByOrg
            };
        },
        async submit() {
            const selectionPayload = this.buildSelectionPayload();
            Object.assign(this.dialogState, selectionPayload);
            if (typeof this.dialogState.processSelection !== 'function') {
                library.hideDialog(this.contextDialogId, true);
                return;
            }
            this.processing = true;
            this.processResult = false;
            this.dialogState.response = false;
            try {
                const result = await this.dialogState.processSelection(selectionPayload);
                this.dialogState.response = result.response;
                const fallbackMessage = result.success ? 'The assignments were removed.' : 'There was a problem removing these assignments. Review your selection and try again.';
                this.processResult = {
                    success: !!result.success,
                    title: result.title || (result.success ? 'Assignments removed' : 'Assignments weren\'t removed'),
                    messageLines: result.messageLines || [result.message || fallbackMessage]
                };
            } catch (error) {
                this.dialogState.response = false;
                this.processResult = {
                    success: false,
                    title: 'Assignments weren\'t removed',
                    messageLines: ['There was a problem removing these assignments. Review your selection and try again.']
                };
            } finally {
                this.processing = false;
            }
        },
        backToSelection() {
            this.processResult = false;
        },
        finish() {
            library.hideDialog(this.contextDialogId, true);
        }
    },
    components: {
        control: defineAsyncComponent(() => import('@common/control/control.vue'))
    }
};
componentStore.initComponent(unRefPlacePickerCmp, unRefPlacePickerConfig);

export { unRefPlacePickerConfig, unRefPlacePickerCmp };
export default unRefPlacePickerCmp;
</script>

<style scoped>
.urp-dialog-body {
    margin-top: var(--space-0);
}
.urp-text {
    color: var(--text-color-body);
    font-size: var(--text-size-sm);
}
.urp-loading {
    display: flex;
    min-height: calc(var(--space-10) * 4);
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: var(--space-4);
    color: var(--text-color-body);
    text-align: center;
}
.urp-loading-icon {
    color: var(--surface-default-primary);
    font-size: var(--text-size-h3);
}
.urp-loading-copy {
    display: flex;
    flex-direction: column;
    gap: var(--space-1);
}
.urp-loading-text {
    margin: var(--space-0);
}
.urp-helper-text {
    margin: var(--space-1) var(--space-0) var(--space-0);
    color: var(--text-color-subtitle);
}
.urp-empty {
    display: flex;
    flex-direction: column;
    gap: var(--space-5);
    padding: var(--space-2) var(--space-0);
}
.urp-empty-text {
    margin: var(--space-0);
}
.urp-mode-picker :deep(.checkbox-list-input),
.urp-list :deep(.checkbox-list-input) {
    margin: var(--space-0) !important;
}
.urp-mode-picker :deep(.checkboxes-container),
.urp-list :deep(.checkboxes-container) {
    margin: var(--space-0);
    padding: var(--space-0);
    gap: var(--space-2);
}
.urp-mode-picker :deep(.checkbox-wrapper),
.urp-list :deep(.checkbox-wrapper) {
    list-style: none;
}
.urp-mode-picker :deep(.checkbox-wrapper) {
    margin: var(--space-0) var(--space-0) var(--space-2);
}
.urp-divider {
    border: none;
    border-top: 1px solid var(--border-color-default);
    margin: var(--space-3) var(--space-0) var(--space-2);
}
.urp-mode-section {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
}
.urp-mode-empty {
    margin: var(--space-0);
    padding: var(--space-4);
    background: var(--surface-subtle);
    border: 1px solid var(--border-color-default);
    border-radius: var(--radius-sm);
}
.urp-list-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--space-4);
}
.urp-section-label {
    color: var(--text-color-subtitle);
    font-size: var(--text-size-xs);
    font-weight: var(--text-weight-semibold);
}
.urp-count {
    padding: var(--space-1) var(--space-3);
    border: 1px solid var(--border-color-default);
    border-radius: var(--radius-full);
    background: var(--surface-subtle);
    color: var(--text-color-subtitle);
    font-size: var(--text-size-xs);
    font-weight: var(--text-weight-medium);
}
.urp-list {
    max-height: calc(var(--space-10) * 6);
    overflow-y: auto;
    border: 1px solid var(--border-color-default);
    border-radius: var(--radius-md);
    background: var(--surface-default);
    padding: var(--space-2);
}
.urp-list :deep(.checkbox-wrapper) {
    margin: var(--space-0);
    padding: var(--space-2) var(--space-3);
    border-radius: var(--radius-xs);
}
.urp-list :deep(.checkbox-wrapper:hover) {
    background: var(--surface-subtle);
}
.urp-list :deep(.e-checkbox-wrapper) {
    width: 100%;
}
.urp-warning,
.urp-error,
.urp-success {
    display: flex;
    gap: var(--space-3);
    align-items: flex-start;
    background: var(--surface-subtle-warning);
    border: 1px solid var(--border-color-light-warning);
    border-radius: var(--radius-md);
    padding: var(--space-4);
}
.urp-error {
    background: var(--surface-subtle-danger);
    border-color: var(--border-color-light-danger);
}
.urp-success {
    background: var(--surface-subtle-success);
    border-color: var(--border-color-light-success);
}
.urp-warning-icon {
    color: var(--border-color-dark-warning);
    margin-top: var(--space-1);
}
.urp-error .urp-warning-icon {
    color: var(--border-color-dark-danger);
}
.urp-success-icon {
    color: var(--border-color-dark-success);
    margin-top: var(--space-1);
}
.urp-result-title {
    margin: var(--space-0) var(--space-0) var(--space-1);
    color: var(--text-color-body);
    font-size: var(--text-size-sm);
    font-weight: var(--text-weight-semibold);
}
.urp-warning-text {
    margin: var(--space-0);
    color: var(--text-color-body);
    font-size: var(--text-size-sm);
}
.urp-actions {
    margin-top: var(--space-5);
    padding-top: var(--space-4);
    border-top: 1px solid var(--border-color-default);
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: var(--space-3);
}
.urp-empty .urp-actions {
    margin-top: var(--space-0);
}
.urp-btn-secondary,
.urp-btn-primary,
.urp-btn-danger {
    display: inline-flex;
    min-height: var(--space-9);
    align-items: center;
    justify-content: center;
    padding: var(--space-3) var(--space-5);
    border-radius: var(--radius-sm);
    font-size: var(--text-size-sm);
    font-weight: var(--text-weight-medium);
    line-height: 1.2;
    cursor: pointer;
    transition:
        background-color var(--transition-default),
        border-color var(--transition-default),
        color var(--transition-default),
        box-shadow var(--transition-default);
}
.urp-btn-secondary {
    border: 1px solid var(--border-color-default);
    background: var(--surface-default);
    color: var(--text-color-body);
}
.urp-btn-primary,
.urp-btn-danger {
    border: 1px solid transparent;
    color: var(--text-color-inverse);
}
.urp-btn-primary {
    background: var(--surface-default-primary);
}
.urp-btn-danger {
    background: var(--surface-default-danger);
}
.urp-btn-secondary:hover {
    border-color: var(--border-color-disabled);
    background: var(--surface-subtle);
}
.urp-btn-primary:not(:disabled):hover {
    background: var(--surface-dark-primary);
    box-shadow: var(--shadow-1);
}
.urp-btn-danger:not(:disabled):hover {
    background: var(--surface-dark-danger);
    box-shadow: var(--shadow-1);
}
.urp-btn-secondary:focus-visible,
.urp-btn-primary:focus-visible,
.urp-btn-danger:focus-visible {
    outline: var(--space-1) solid var(--border-color-default-primary);
    outline-offset: var(--space-1);
}
.urp-btn-primary:disabled,
.urp-btn-danger:disabled {
    background: var(--surface-disabled);
    color: var(--text-color-disabled);
    border: 1px solid var(--border-color-disabled);
    cursor: not-allowed;
    box-shadow: none;
}
@media (prefers-reduced-motion: reduce) {
    .urp-loading-icon {
        animation: none;
    }
}
</style>
